// src/components/views/ThriftView.tsx
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import Masonry from 'react-masonry-css';
import RangeSlider from 'react-range-slider-input';
import 'react-range-slider-input/dist/style.css';
import { 
  ArrowLeft, ShoppingBag, Bell, MessageCircle, 
  Filter, Heart, X, Check, User, ChevronDown, ArrowRight,
  Clock, Store, Shirt, Footprints,
  Gem, Sparkle, Layers, Palette, Crown, Users, Sparkles,
  Home, Search, PlusSquare
} from 'lucide-react';
import { db } from '../../services/firebase';
import { 
  collection, query, where, onSnapshot, doc, getDoc, getDocs 
} from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { ThriftProfileModal } from '../shopping/ThriftProfileModal';
import { ThriftProductDetailModal } from '../shopping/ThriftProductDetailModal';
import { CartOverlay } from '../shopping/CartOverlay';
import { useCart } from '../../contexts/CartContext';
import { Loader2 } from 'lucide-react';

// ----------------------------------------------------------------------
// Collection Card Component
// ----------------------------------------------------------------------
interface CollectionCardProps {
  name: string;
  onClick: () => void;
}

const CollectionCard = ({ name, onClick }: CollectionCardProps) => {
  const getStyleImage = (styleName: string) => {
    const images: Record<string, string> = {
      'Streetwear': 'https://images.unsplash.com/photo-1556905055-8f358a7a47b2?w=500&h=600&fit=crop&crop=center',
      'Luxury': 'https://images.unsplash.com/photo-1532453288672-3a27e9be9efd?w=500&h=600&fit=crop&crop=center',
      'Vintage': 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=500&h=600&fit=crop&crop=center',
      'Minimalist': 'https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?w=500&h=600&fit=crop&crop=center',
      'Y2K': 'https://images.unsplash.com/photo-1578632767115-351597cf2477?w=500&h=600&fit=crop&crop=center',
      'Athleisure': 'https://images.unsplash.com/photo-1518310383802-640c2de311b2?w=500&h=600&fit=crop&crop=center',
      'Casual': 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?w=500&h=600&fit=crop&crop=center',
    };
    return images[styleName] || 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=500&h=600&fit=crop&crop=center';
  };

  return (
    <div onClick={onClick} className="relative group cursor-pointer bg-zinc-900 overflow-hidden aspect-[4/5] shadow-sm">
      <img 
        src={getStyleImage(name)} 
        className="w-full h-full object-cover grayscale group-hover:scale-105 transition-transform duration-700" 
        alt={name} 
      />
      <div className="absolute bottom-3 left-3 right-3 bg-white text-black p-3">
        <h3 className="font-black uppercase tracking-widest text-[11px] mb-2">{name}</h3>
        <div className="flex justify-between items-center text-[8px] font-bold text-black/50 uppercase tracking-widest border-t border-black/10 pt-2 group-hover:text-black transition-colors">
          <span>Shop Now</span>
          <ArrowRight size={10} />
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------
// Main ThriftView Component
// ----------------------------------------------------------------------
interface ThriftItem {
  id: string;
  type: 'thrift';
  itemName: string;
  price: number;
  originalPrice?: number;
  image: string;
  images?: string[];
  condition: string;
  size: string;
  category: string;
  styleCategory?: string;
  userName: string;
  userId: string;
  userAvatar?: string;
  sellerName?: string;
  sellerId?: string;
  sellerAvatar?: string;
  businessName?: string;
  displayName?: string;
  storeName?: string;
  storeId?: string;
  workspaceId?: string;
  brandName?: string;
  status?: string;
  stock?: number;
  hype: number;
  createdAt: any;
  description?: string;
  era?: string;
  location?: string;
  color?: string;
  likes?: string[];
  comments?: number;
  authenticationBadge?: boolean;
  isOneOfOne?: boolean;
}

interface ThriftViewProps {
  wishlist?: string[];
  onToggleWishlist?: (id: string, type: 'product') => void;
  onSelectProduct?: (product: any) => void;
  onOpenSellModal?: () => void;
  onViewProfile?: (userId: string) => void;
  onShareProduct?: (item: any) => void;
}

export const ThriftView: React.FC<ThriftViewProps> = ({
  wishlist = [],
  onToggleWishlist,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser, profile: userProfile } = useAuth();
  const { cartItems, addToCart, removeFromCart, updateQuantity, clearCart } = useCart();
  
  const [thrifts, setThrifts] = useState<ThriftItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeStyleCategory, setActiveStyleCategory] = useState('All');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ minPrice: 0, maxPrice: 5000, condition: '', size: '' });
  const [selectedItem, setSelectedItem] = useState<ThriftItem | null>(null);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const [viewingThrift, setViewingThrift] = useState<any>(null);
  const [thriftProducts, setThriftProducts] = useState<any[]>([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);

  const styleCategories = [
    { name: 'All', icon: <Store size={14} /> },
    { name: 'Streetwear', icon: <Shirt size={14} /> },
    { name: 'Luxury', icon: <Gem size={14} /> },
    { name: 'Vintage', icon: <Clock size={14} /> },
    { name: 'Minimalist', icon: <Layers size={14} /> },
    { name: 'Y2K', icon: <Sparkle size={14} /> },
    { name: 'Athleisure', icon: <Footprints size={14} /> },
    { name: 'Casual', icon: <Palette size={14} /> },
  ];

  const conditions = ['10/10 (Deadstock/New)', '9/10 (Excellent/Like New)', '8/10 (Good/Minor Wear)', '7/10 (Fair/Visible Wear)', 'Vintage/Distressed'];
  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'OS'];

  useEffect(() => {
    const q = query(
      collection(db, 'products'),
      where('workspaceType', '==', 'thrift'),
      where('status', 'in', ['active', 'sold'])
    );
    
    const unsubscribe = onSnapshot(q, async (snapshot) => {
      try {
        const items = await Promise.all(snapshot.docs.map(async (docSnap) => {
          const data = docSnap.data();
          const userId = data.workspaceId || data.sellerId || data.userId || data.businessId;
          
          let sellerName = 'Unknown Seller';
          let sellerId = userId || '';
          let sellerAvatar = '';
          
          if (data.sellerName && data.sellerName !== 'Unknown Seller' && data.sellerName !== 'r') {
            sellerName = data.sellerName;
            sellerId = data.sellerId || userId || '';
            sellerAvatar = data.sellerAvatar || '';
          } else if (data.brandName && data.brandName !== 'r' && data.brandName.length > 1) {
            sellerName = data.brandName;
            sellerId = userId || '';
          } else if (userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                sellerName = userData.displayName || 
                             userData.businessName || 
                             userData.username || 
                             userData.sellerName || 
                             userData.name ||
                             data.brandName ||
                             'Unknown Seller';
                sellerId = userId;
                sellerAvatar = userData.photoURL || userData.logoUrl || userData.avatar || '';
              } else {
                if (data.brandName && data.brandName !== 'r' && data.brandName.length > 1) {
                  sellerName = data.brandName;
                }
              }
            } catch (e) {
              console.error('Error fetching user data:', e);
            }
          }
          
          if (sellerName === 'Unknown Seller' || sellerName === 'r') {
            if (data.brandName && data.brandName !== 'r' && data.brandName.length > 1) {
              sellerName = data.brandName;
            } else {
              sellerName = 'Thrift Seller';
            }
          }
          
          return {
            id: docSnap.id,
            type: 'thrift',
            itemName: data.name || data.itemName || 'Unnamed',
            price: data.price || 0,
            originalPrice: data.originalPrice || data.salePrice,
            image: data.image || (data.images && data.images[0]) || '',
            images: data.images || [],
            condition: data.condition || '',
            size: data.size || data.stockVariants?.[0]?.size || '',
            category: data.category || '',
            styleCategory: data.styleCategory || '',
            userName: sellerName,
            userId: sellerId,
            userAvatar: sellerAvatar,
            sellerName: sellerName,
            sellerId: sellerId,
            sellerAvatar: sellerAvatar,
            businessName: sellerName,
            displayName: sellerName,
            storeName: sellerName,
            storeId: sellerId,
            workspaceId: userId,
            brandName: data.brandName || data.brand,
            status: data.status || 'active',
            stock: data.stock || 1,
            hype: data.hype || Math.floor(Math.random() * 50) + 1,
            createdAt: data.createdAt,
            description: data.description || data.caption,
            era: data.era,
            location: data.location,
            color: data.color || data.stockVariants?.[0]?.color || '',
            likes: data.likes || [],
            comments: data.comments || 0,
            authenticationBadge: data.authenticationBadge || false,
            isOneOfOne: data.isOneOfOne || true,
          } as ThriftItem;
        }));
        
        items.sort((a, b) => {
          const dateA = a.createdAt?.toMillis ? a.createdAt.toMillis() : (a.createdAt?.seconds ? a.createdAt.seconds * 1000 : (a.createdAt || 0));
          const dateB = b.createdAt?.toMillis ? b.createdAt.toMillis() : (b.createdAt?.seconds ? b.createdAt.seconds * 1000 : (b.createdAt || 0));
          return dateB - dateA;
        });
        
        setThrifts(items);
        setIsLoading(false);
      } catch (error) {
        console.error('Error processing thrift items:', error);
        setIsLoading(false);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const handleAddToCart = (item: any) => {
    const cartItem = {
      id: item.id,
      name: item.itemName || item.name,
      price: item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      brand: item.sellerName || item.userName || item.brandName || 'Thrift Seller',
      selectedSize: item.size || 'OS',
      selectedColor: item.color || 'Standard',
      sellerId: item.sellerId || item.userId || item.workspaceId,
      sellerName: item.sellerName || item.userName || item.brandName || 'Thrift Seller',
      sellerAvatar: item.sellerAvatar || item.userAvatar || '',
      workspaceType: 'thrift',
      type: 'thrift',
      workspaceId: item.sellerId || item.userId || item.workspaceId,
      businessId: item.sellerId || item.userId || item.workspaceId,
      condition: item.condition || '',
      era: item.era || '',
      styleCategory: item.styleCategory || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      stock: item.stock || 1,
      originalItem: item
    };
    addToCart(cartItem);
    showToast('Added to cart! 🛒');
  };

  const handleBuyNow = (item: any) => {
    const cartItem = {
      id: item.id,
      name: item.itemName || item.name,
      price: item.price,
      image: item.image,
      images: item.images || [],
      quantity: 1,
      brand: item.sellerName || item.userName || item.brandName || 'Thrift Seller',
      selectedSize: item.size || 'OS',
      selectedColor: item.color || 'Standard',
      sellerId: item.sellerId || item.userId || item.workspaceId,
      sellerName: item.sellerName || item.userName || item.brandName || 'Thrift Seller',
      sellerAvatar: item.sellerAvatar || item.userAvatar || '',
      workspaceType: 'thrift',
      type: 'thrift',
      workspaceId: item.sellerId || item.userId || item.workspaceId,
      businessId: item.sellerId || item.userId || item.workspaceId,
      condition: item.condition || '',
      era: item.era || '',
      styleCategory: item.styleCategory || '',
      size: item.size || '',
      color: item.color || '',
      authenticationBadge: item.authenticationBadge || false,
      isOneOfOne: true,
      stock: item.stock || 1,
      originalItem: item
    };
    clearCart();
    addToCart(cartItem);
    setIsCartOpen(true);
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) {
      showToast('Your cart is empty.');
      return;
    }
  };

  const showToast = (message: string) => {
    window.dispatchEvent(new CustomEvent('show-toast', { detail: { message } }));
  };

  const handleViewThriftProfile = async (id: string) => {
    if (!id) {
      alert('Unable to load profile: No ID provided');
      return;
    }
    
    setIsProfileLoading(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', id));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        setViewingThrift({
          id: userDoc.id,
          uid: userDoc.id,
          ...userData,
          businessName: userData.businessName || userData.displayName || userData.username || 'Thrift Store',
          displayName: userData.displayName || userData.businessName || userData.username || 'Thrift Store',
          location: userData.location || userData.sellerLocation || 'Global',
          photoURL: userData.photoURL || userData.logoUrl || null,
          logoUrl: userData.logoUrl || userData.photoURL || null,
          bannerUrl: userData.bannerUrl || null,
          bio: userData.description || userData.bio || '',
          description: userData.description || userData.bio || '',
          theme: userData.theme || userData.storefront?.theme || 'vintage',
          storefront: userData.storefront || {},
          verified: userData.verified || false,
          followersCount: userData.followersCount || userData.followers || 0,
          showAuthenticationBadge: userData.showAuthenticationBadge !== undefined ? userData.showAuthenticationBadge : true,
          showConditionGrades: userData.showConditionGrades !== undefined ? userData.showConditionGrades : true,
          featuredEra: userData.featuredEra || '90s',
          curatorsNote: userData.curatorsNote || '',
          layout: userData.layout || 'grid',
          primaryColor: userData.primaryColor || '#000000',
          province: userData.province || 'Gauteng',
          instagramHandle: userData.instagramHandle || '',
          tiktokHandle: userData.tiktokHandle || '',
          twitterHandle: userData.twitterHandle || '',
          facebookHandle: userData.facebookHandle || '',
          youtubeHandle: userData.youtubeHandle || '',
          footerText: userData.footerText || '',
          footerSocialLinks: userData.footerSocialLinks || {},
          showStoreName: userData.showStoreName !== undefined ? userData.showStoreName : true,
          showFollowButton: userData.showFollowButton !== undefined ? userData.showFollowButton : true,
          showContactButton: userData.showContactButton !== undefined ? userData.showContactButton : true,
          showShareButton: userData.showShareButton !== undefined ? userData.showShareButton : true,
          showReportButton: userData.showReportButton !== undefined ? userData.showReportButton : true,
        });
        
        const productsQuery = query(
          collection(db, 'products'),
          where('workspaceId', '==', id)
        );
        const productsSnap = await getDocs(productsQuery);
        const productList = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setThriftProducts(productList);
      } else {
        alert('This seller\'s profile could not be found.');
      }
    } catch (error) {
      console.error('Error fetching thrift profile:', error);
      alert('Error loading profile. Please try again.');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleOpenProductFromProfile = (product: any) => {
    setSelectedItem(product);
  };

  const handleSendMessageToThrift = (sellerId: string, sellerName: string) => {
    if (!currentUser) {
      alert('Please log in to message this seller.');
      return;
    }
    window.dispatchEvent(new CustomEvent('open-messages', {
      detail: { userId: sellerId }
    }));
  };

  const { uniqueSellers } = useMemo(() => {
    const sellersMap = new Map<string, { name: string; userId: string; avatar?: string; itemCount: number }>();

    thrifts.forEach((item) => {
      const sellerId = item.userId || item.sellerId || item.storeId || item.workspaceId;
      const sellerName = item.userName || 
                         item.sellerName || 
                         item.brandName || 
                         item.businessName || 
                         item.displayName || 
                         item.storeName;
      if (sellerName && sellerId && sellerName !== 'Unknown Seller' && sellerName !== 'r') {
        const key = sellerId;
        if (!sellersMap.has(key)) {
          sellersMap.set(key, {
            name: sellerName,
            userId: sellerId,
            avatar: item.userAvatar || item.sellerAvatar,
            itemCount: 0,
          });
        }
        const seller = sellersMap.get(key)!;
        seller.itemCount += 1;
      }
    });

    return {
      uniqueSellers: Array.from(sellersMap.values()).sort((a, b) => b.itemCount - a.itemCount),
      topSeller: null,
    };
  }, [thrifts]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sharedPostId = params.get('post');
    if (sharedPostId && thrifts.length > 0) {
      const found = thrifts.find(p => p.id === sharedPostId);
      if (found) {
        setSelectedItem(found);
        window.history.replaceState({}, '', '/thrifts');
      }
    }
  }, [thrifts, location.search]);

  const filteredItems = useMemo(() => {
    return thrifts.filter(item => {
      const matchesSearch = item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            item.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (item.brandName && item.brandName.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesCategory = activeStyleCategory === 'All' || item.styleCategory === activeStyleCategory;
      const price = item.price;
      const matchesPrice = price >= filters.minPrice && price <= filters.maxPrice;
      const matchesCondition = !filters.condition || item.condition === filters.condition;
      const matchesSize = !filters.size || item.size === filters.size;
      return matchesSearch && matchesCategory && matchesPrice && matchesCondition && matchesSize;
    });
  }, [thrifts, searchTerm, activeStyleCategory, filters]);

  const isWishlisted = (id: string) => wishlist.includes(id);
  const breakpointColumns = { default: 4, 1024: 4, 768: 2, 500: 2 };
  
  const cartTotal = cartItems.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const cartCount = cartItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  const requireAuth = (fn: () => void, message: string) => {
    if (!currentUser) {
      alert(message || 'Please log in to continue.');
      return;
    }
    fn();
  };

  if (isLoading && thrifts.length === 0) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-sans pb-16 relative">
      
      {/* =========================================================================
          🖥️ DESKTOP HEADER - FIXED GAMES TAB
      ========================================================================= */}
      <header className="hidden lg:flex fixed top-0 left-0 right-0 z-50 bg-black border-b border-zinc-800 flex-col">
        
        {/* Top Bar - Free Shipping */}
        <div className="flex justify-between items-center py-2 px-8 border-b border-zinc-800 text-[10px] uppercase tracking-widest font-bold text-zinc-500">
          <span>Thrift Closet</span>
          <span>Free Shipping on Orders over R1000</span>
        </div>

        {/* Main Header */}
        <div className="flex items-center justify-between h-20 px-8">
          {/* Left Nav - Brands | Jewellery | Thrift | Games | Designer | Collections | Community */}
          <nav className="flex gap-6 items-center">
            {['Brands', 'Jewellery', 'Thrift', , 'Designer',].map((link) => {
              let path = '/';
              if (link === 'Brands') path = '/';
              else if (link === 'Jewellery') path = '/jewellery';
              else if (link === 'Thrift') path = '/thrifts';
              else if (link === 'Games') path = '/games';  // ✅ FIXED
              else if (link === 'Designer') path = '/designer';
              
              return (
                <NavLink 
                  key={link} 
                  to={path}
                  className={({isActive}) => `text-xs font-black uppercase tracking-[0.2em] hover:text-white transition-colors whitespace-nowrap ${isActive ? 'text-white underline decoration-2 underline-offset-4' : 'text-zinc-500'}`}
                >
                  {link}
                </NavLink>
              );
            })}
          </nav>

          {/* Center Logo - THRIFT */}
          <div className="absolute left-1/2 -translate-x-1/2">
            <h1 className="text-5xl font-black italic tracking-tighter cursor-pointer" onClick={() => navigate('/thrifts')}>
              THRIFT<span className="text-zinc-800">.</span>
            </h1>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-6">
            <NavLink to="/search" className="text-zinc-500 hover:text-white transition-colors" title="Search">
              <Search size={20} />
            </NavLink>
            
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="text-zinc-500 hover:text-white transition-colors relative" title="Messages">
              <MessageCircle size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="text-zinc-500 hover:text-white transition-colors relative" title="Notifications">
              <Bell size={20} />
            </button>

            <button onClick={() => window.dispatchEvent(new CustomEvent('open-creation-menu'))} className="flex items-center gap-2 text-zinc-500 hover:text-white transition-colors" title="Create Post">
              <PlusSquare size={20} />
            </button>

            <NavLink to="/profile" className="text-xs font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors">
              {currentUser ? `Account (${userProfile?.cred || 0})` : 'Login'}
            </NavLink>
            
            <button onClick={() => setIsCartOpen(true)} className={`flex items-center gap-2 transition-colors ${cartCount > 0 ? 'text-white' : 'text-zinc-500 hover:text-white'}`}>
              <ShoppingBag size={20} />
              <span className="text-xs font-black">({cartCount})</span>
            </button>
          </div>
        </div>
      </header>

      {/* =========================================================================
          📱 MOBILE HEADER - FIXED GAMES TAB
      ========================================================================= */}
      <header className="lg:hidden sticky top-0 z-40 bg-black/95 backdrop-blur-xl border-b border-zinc-800">
        {/* Top Row - Logo and Icons */}
        <div className="flex items-center justify-between px-4 py-3">
          <h1 className="text-xl font-black italic tracking-tighter cursor-pointer" onClick={() => navigate('/thrifts')}>
            THRIFT<span className="text-zinc-800">.</span>
          </h1>
          
          <div className="flex gap-4 items-center text-zinc-300">
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-messages'))} className="hover:text-white transition-colors relative">
              <MessageCircle size={20} />
            </button>
            <button onClick={() => window.dispatchEvent(new CustomEvent('open-notifications'))} className="hover:text-white transition-colors relative">
              <Bell size={20} />
            </button>
            
            <button onClick={() => setIsCartOpen(true)} className="hover:text-white transition-colors relative">
              <ShoppingBag size={20} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-white text-black text-[8px] font-black w-4 h-4 rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile Tabs - Order: Brands, Jewellery, Thrift, Games, Designer */}
        <div className="flex overflow-x-auto scrollbar-hide border-t border-white/5">
          {['Brands', 'Jewellery', 'Thrift', , 'Designer'].map((tab) => {
            let path = '/';
            let isActive = false;
            
            if (tab === 'Brands') { 
              path = '/'; 
              isActive = location.pathname === '/'; 
            }
            else if (tab === 'Jewellery') { 
              path = '/jewellery'; 
              isActive = location.pathname === '/jewellery'; 
            }
            else if (tab === 'Thrift') { 
              path = '/thrifts'; 
              isActive = location.pathname === '/thrifts'; 
            }
            else if (tab === 'Games') { 
              path = '/games';  // ✅ FIXED
              isActive = location.pathname === '/games'; 
            }
            else if (tab === 'Designer') { 
              path = '/designer'; 
              isActive = location.pathname === '/designer'; 
            }
            
            return (
              <NavLink
                key={tab}
                to={path}
                className={({isActive: isNavActive}) => {
                  const active = isNavActive || isActive;
                  return `flex-1 text-center px-2 py-2.5 text-[10px] font-black uppercase tracking-wider transition-all duration-200 whitespace-nowrap ${
                    active ? 'text-white border-b-2 border-white' : 'text-zinc-500 hover:text-zinc-300'
                  }`;
                }}
              >
                {tab}
              </NavLink>
            );
          })}
        </div>
      </header>

      {/* =========================================================================
          MAIN CONTENT - With top padding for desktop header
      ========================================================================= */}
      <div className="lg:pt-[105px] pt-0">
        
        {/* HERO SECTION */}
        <div className="px-4 md:px-8 my-4 max-w-6xl mx-auto">
          <div className="w-full h-[320px] md:h-[480px] relative bg-zinc-900 overflow-hidden">
            <img 
              src="https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=2070&auto=format&fit=crop" 
              className="w-full h-full object-cover grayscale" 
              alt="Hero" 
            />
            {/* Overlay Swipe Box */}
            <div className="absolute bottom-0 left-0 bg-white text-black p-4 md:p-6 w-56 md:w-72 m-3 md:m-8 shadow-2xl">
              <p className="font-black uppercase tracking-widest text-xs mb-4">Swipe</p>
              <div className="flex justify-between items-center border-t border-black/20 pt-3 cursor-pointer hover:opacity-60 transition-opacity">
                <span className="font-black text-[9px] text-black/60 uppercase tracking-widest">Discover Now</span>
                <ArrowRight size={12} />
              </div>
            </div>
          </div>
        </div>

        {/* SHOP BY COLLECTION */}
        <div className="px-4 md:px-8 max-w-6xl mx-auto mb-8">
          <div className="mb-4 border-b border-white/10 pb-2">
            <h2 className="text-xs md:text-sm font-black uppercase tracking-widest">Shop By Collection</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {styleCategories.slice(1, 5).map(cat => (
              <CollectionCard key={cat.name} name={cat.name} onClick={() => setActiveStyleCategory(cat.name)} />
            ))}
          </div>
        </div>

        {/* IN-PAGE CATEGORIES & LINKS */}
        <div className="px-4 md:px-8 max-w-6xl mx-auto mb-10">
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 py-4 border-y border-white/10">
            <button className="text-[10px] md:text-xs font-black uppercase tracking-widest text-white hover:text-white/60 flex items-center gap-1 transition-colors">
              Categories <ChevronDown size={12}/>
            </button>
            <button className="text-[10px] md:text-xs font-black uppercase tracking-widest text-white hover:text-white/60 flex items-center gap-1 transition-colors">
              New Arrivals <ChevronDown size={12}/>
            </button>
            <span className="hidden md:block w-px h-4 bg-white/20" />
            {['Men', 'Women', 'Brands'].map(cat => (
              <button key={cat} className="text-[10px] md:text-xs font-black uppercase tracking-widest text-white/80 hover:text-white transition-colors">
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* BEST SELLERS GRID */}
        <div className="px-4 md:px-8 max-w-6xl mx-auto">
          <div className="flex justify-between items-end mb-4 border-b border-white/10 pb-2">
            <h2 className="text-xs md:text-sm font-black uppercase tracking-widest">Best Sellers</h2>
            <button onClick={() => setIsFilterOpen(true)} className="text-[10px] font-black uppercase text-white hover:text-white/60 flex items-center gap-1.5 tracking-widest transition-colors">
              <Filter size={12} /> Filter
            </button>
          </div>

          {filteredItems.length === 0 ? (
            <div className="text-center py-12 text-white/40 font-bold uppercase tracking-widest text-xs">No thrift pieces found in this vibe.</div>
          ) : (
            <Masonry breakpointCols={breakpointColumns} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
              {filteredItems.map(item => {
                const isSold = item.status === 'sold' || item.stock === 0;
                return (
                  <div key={item.id} className="mb-6 group cursor-pointer" onClick={() => setSelectedItem(item)}>
                    <div className="bg-zinc-900 overflow-hidden relative mb-2 aspect-[4/5] flex items-center justify-center p-4 border border-white/5 group-hover:border-white/20 transition-all">
                      <img 
                        src={item.image} 
                        className="w-full h-full object-cover grayscale mix-blend-luminosity group-hover:mix-blend-normal group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" 
                        alt={item.itemName} 
                      />
                      {isSold && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-sm font-black text-white uppercase tracking-widest border-2 border-white px-4 py-1 rotate-[-10deg]">SOLD</span>
                        </div>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onToggleWishlist?.(item.id, 'product'); }}
                        className="absolute top-3 right-3 p-1.5 bg-white/10 backdrop-blur-md rounded-full shadow-sm hover:scale-110 transition-all border border-white/10 opacity-0 group-hover:opacity-100"
                      >
                        <Heart size={12} fill={isWishlisted(item.id) ? 'white' : 'none'} className={isWishlisted(item.id) ? 'text-white' : 'text-white/40'} />
                      </button>
                    </div>
                    
                    <div className="flex justify-between items-start px-1">
                      <div className="pr-2">
                        <h4 className="font-black text-[11px] text-white uppercase tracking-widest truncate max-w-[130px]">{item.itemName}</h4>
                        <p className="text-white/40 text-[8px] font-bold uppercase tracking-widest mt-0.5">
                          {item.brandName || item.sellerName || 'Thrift Find'}
                        </p>
                      </div>
                      <p className="font-mono font-black text-xs text-white shrink-0">
                        {isSold ? 'SOLD' : `R${item.price.toFixed(2)}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </Masonry>
          )}
        </div>
      </div>

      {/* MODALS AND OVERLAYS */}

      {/* Floating Cart Mobile */}
      {cartCount > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-black/95 border-t border-white/10 p-3 flex items-center justify-between backdrop-blur-xl animate-in slide-in-from-bottom">
          <div>
            <p className="text-[9px] text-white/50 font-black uppercase tracking-widest">Cart</p>
            <p className="text-white font-black text-xs">{cartCount} items • R{cartTotal.toFixed(2)}</p>
          </div>
          <button
            onClick={() => setIsCartOpen(true)}
            className="px-5 py-2.5 bg-white text-black font-black uppercase tracking-widest text-[9px] hover:bg-zinc-200 transition-colors"
          >
            View Cart
          </button>
        </div>
      )}

      {/* THRIFT PROFILE MODAL */}
      {viewingThrift && (
        <ThriftProfileModal
          isOpen={!!viewingThrift}
          onClose={() => {
            setViewingThrift(null);
            setThriftProducts([]);
          }}
          brand={viewingThrift}
          products={thriftProducts}
          onSelectProduct={handleOpenProductFromProfile}
          onSendMessage={handleSendMessageToThrift}
        />
      )}

      {/* THRIFT PRODUCT DETAIL MODAL */}
      <ThriftProductDetailModal
        item={selectedItem}
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        onToggleWishlist={onToggleWishlist}
        isWishlisted={selectedItem ? isWishlisted(selectedItem.id) : false}
        userProfile={userProfile}
        currentUser={currentUser}
        requireAuth={requireAuth}
        onViewProfile={handleViewThriftProfile}
        onSendMessage={handleSendMessageToThrift}
        onAddToCart={handleAddToCart}
        onBuyNow={handleBuyNow}
        cartItems={cartItems}
      />

      {/* CART OVERLAY */}
      <CartOverlay
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cartItems}
        updateQuantity={updateQuantity}
        remove={removeFromCart}
        checkout={handleCheckout}
        getSellerType={(sellerId) => {
          const seller = uniqueSellers.find(s => s.userId === sellerId);
          return seller ? 'thrift' : 'brand';
        }}
        onOpenChat={(sellerId, sellerName) => {
          handleSendMessageToThrift(sellerId, sellerName);
        }}
      />

      {/* Loading overlay */}
      {isProfileLoading && (
        <div className="fixed inset-0 z-[999] bg-black/80 flex items-center justify-center">
          <Loader2 className="animate-spin text-white" size={48} />
        </div>
      )}

      {/* Filter Modal */}
      {isFilterOpen && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in" onClick={() => setIsFilterOpen(false)} />
          <div className="bg-black w-full p-6 pb-safe relative z-10 animate-in slide-in-from-bottom-full duration-300 max-h-[85vh] overflow-y-auto border-t border-white/20">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-black uppercase tracking-tighter text-white">Refine</h2>
              <button onClick={() => setIsFilterOpen(false)} className="p-2 bg-white/10 border border-white/10 hover:bg-white hover:text-black transition-colors text-white"><X size={18} /></button>
            </div>
            <div className="mb-8">
              <div className="flex justify-between items-end mb-4">
                <h3 className="text-[10px] font-black uppercase tracking-widest text-white/40">Price Range (ZAR)</h3>
                <span className="text-xs font-mono font-black text-white">R {filters.minPrice} - R {filters.maxPrice}</span>
              </div>
              <RangeSlider min={0} max={5000} step={50} value={[filters.minPrice, filters.maxPrice]} onInput={(val: any) => setFilters({...filters, minPrice: val[0], maxPrice: val[1]})} />
            </div>
            <div className="mb-8">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-4">Condition</h3>
              <div className="flex flex-wrap gap-2">
                {conditions.map(cond => (
                  <button key={cond} onClick={() => setFilters({...filters, condition: filters.condition === cond ? '' : cond})} className={`px-4 py-2.5 border text-[9px] font-black uppercase tracking-widest flex items-center gap-2 transition-colors ${filters.condition === cond ? 'border-white bg-white text-black' : 'border-white/20 text-white/60 bg-transparent hover:border-white'}`}>
                    {filters.condition === cond && <Check size={10}/>} {cond.replace(/\/.*$/, '').trim()}
                  </button>
                ))}
              </div>
            </div>
            <div className="mb-8">
              <h3 className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-4">Size</h3>
              <div className="flex flex-wrap gap-2">
                {sizes.map(size => (
                  <button key={size} onClick={() => setFilters({...filters, size: filters.size === size ? '' : size})} className={`w-12 h-12 border text-xs font-black flex items-center justify-center transition-colors ${filters.size === size ? 'border-white bg-white text-black' : 'border-white/20 text-white/60 bg-transparent hover:border-white'}`}>
                    {size}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-4 pt-6 border-t border-white/10">
              <button onClick={() => setFilters({minPrice: 0, maxPrice: 5000, condition: '', size: ''})} className="px-6 py-3 font-black uppercase tracking-widest text-[9px] text-white/40 hover:text-white">Reset</button>
              <button onClick={() => setIsFilterOpen(false)} className="flex-1 bg-white text-black font-black uppercase tracking-widest text-[10px] py-3 hover:bg-zinc-200 transition-colors">Show {filteredItems.length} Pieces</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThriftView;